/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import {
  ESCENARIOS_CAPTACION,
  INVERSION_REFERENCIA_3M,
  IPSOS_PERFUMES,
  TICKETS_PERFUME,
} from "../../data/ipsos2025/perfumes";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaPerfumes.module.scss";

export const Perfumes = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("Rabanne");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [ticketPromedio, setTicketPromedio] = useState(75000);
  const [verAnual, setVerAnual] = useState(false);

  // =====================================================
  // CÁLCULOS PRINCIPALES
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const usanPerfume = Math.round(totalEjecutivos * IPSOS_PERFUMES.penetracion.valor);
    const noUsan = Math.round(totalEjecutivos * IPSOS_PERFUMES.noPenetracion.valor);

    // Desglose por frecuencia de compra
    const desgloseFrecuencia = {};
    let comprasAnuales = 0;

    Object.entries(IPSOS_PERFUMES.frecuenciaCompra).forEach(([freq, data]) => {
      const personas = Math.round(usanPerfume * data.pct.valor);
      const compras = Math.round(personas * data.factor.valor);

      desgloseFrecuencia[freq] = {
        pct: data.pct.valor,
        pctDato: data.pct,
        label: data.label,
        personas,
        compras,
      };

      comprasAnuales += compras;
    });

    const comprasMensuales = Math.round(comprasAnuales / 12);
    const mercadoAnual = comprasAnuales * ticketPromedio;
    const mercadoMensual = Math.round(mercadoAnual / 12);

    // Market share por marca (sorted by share descending)
    const marketShareData = Object.entries(IPSOS_PERFUMES.marcas)
      .sort(([, a], [, b]) => b.valor - a.valor)
      .map(([nombre, dato]) => ({
        nombre,
        dato,
        share: dato.valor,
        clientes: Math.round(usanPerfume * dato.valor),
        mercadoMensual: Math.round(mercadoMensual * dato.valor),
      }));

    // Desglose por prioridad
    const prioridades = {};
    Object.entries(IPSOS_PERFUMES.prioridades).forEach(([prioridad, dato]) => {
      prioridades[prioridad] = {
        dato,
        personas: Math.round(usanPerfume * dato.valor),
        pct: dato.valor * 100,
      };
    });

    // Desglose por cantidad de perfumes
    const cantidadPerfumes = {};
    Object.entries(IPSOS_PERFUMES.cantidadPerfumes).forEach(([cantidad, dato]) => {
      cantidadPerfumes[cantidad] = {
        dato,
        personas: Math.round(usanPerfume * dato.valor),
        pct: dato.valor * 100,
      };
    });

    return {
      usanPerfume,
      noUsan,
      desgloseFrecuencia,
      comprasMensuales,
      comprasAnuales,
      mercadoMensual,
      mercadoAnual,
      marketShareData,
      prioridades,
      cantidadPerfumes,
    };
  }, [totalEjecutivos, ticketPromedio]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN
  // =====================================================
  const calcularOportunidad = useCallback(
    (marca, pctCaptacion) => {
      if (!resultados) return null;

      const mercadoCaptadoMensual = Math.round(resultados.mercadoMensual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultados.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        pctDelTotal: pctCaptacion,
      };
    },
    [resultados]
  );

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  const frecuenciaKeys = Object.keys(IPSOS_PERFUMES.frecuenciaCompra);

  return (
    <div className={styles.perfumesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>PERFUMES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.perfumesContent}>
        {/* Tabla de frecuencia de compra */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thNoCompran)}>No usan</th>
                  <th className={cx("text-center", styles.thSiCompran)}>Sí usan</th>
                  <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                  <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                  <th className={cx("text-center", styles.thConversiones)}>Conversiones {verAnual ? "año" : "mes"}</th>
                </tr>
              </thead>
              <tbody>
                {frecuenciaKeys.map((freq, idx) => {
                  const data = resultados.desgloseFrecuencia[freq];
                  return (
                    <tr key={freq}>
                      {idx === 0 && (
                        <>
                          <td
                            rowSpan={frecuenciaKeys.length}
                            className={cx("text-center align-middle", styles.celdaNo)}
                          >
                            <div className={styles.celdaContenido}>
                              <BadgeDato dato={IPSOS_PERFUMES.noPenetracion} className={styles.badgeOportunidad}>
                                {(IPSOS_PERFUMES.noPenetracion.valor * 100).toFixed(0)}%
                              </BadgeDato>
                              <div className={styles.valorMuyGrande}>{formatNumber(resultados.noUsan)}</div>
                              <div className={styles.subtextoOportunidad}>OPORTUNIDAD</div>
                            </div>
                          </td>
                          <td
                            rowSpan={frecuenciaKeys.length}
                            className={cx("text-center align-middle", styles.celdaSi)}
                          >
                            <div className={styles.celdaContenido}>
                              <BadgeDato dato={IPSOS_PERFUMES.penetracion}>
                                {(IPSOS_PERFUMES.penetracion.valor * 100).toFixed(0)}%
                              </BadgeDato>
                              <div className={styles.valorMuyGrande}>{formatNumber(resultados.usanPerfume)}</div>
                            </div>
                          </td>
                        </>
                      )}
                      <td className={styles.celdaFrecuencia}>
                        <div className={styles.frecuenciaInner}>
                          <BadgeDato dato={data.pctDato}>{(data.pct * 100).toFixed(0)}%</BadgeDato>
                          <span>{data.label}</span>
                        </div>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">
                        {freq === "noCompra" ? "-" : formatNumber(verAnual ? data.compras * 12 : data.compras)}
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.filaTotales}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.comprasAnuales : resultados.comprasMensuales)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Ticket + Mercado + Toggle */}
        {resultados && (
          <div className={styles.dosBloquesContainer}>
            {/* Ticket promedio */}
            <div className={styles.bloqueTicket}>
              <div className={styles.ticketLabel}>Ticket promedio (supuesto)</div>
              <div className={styles.ticketBox}>
                <div className={styles.ticketOpciones}>
                  {TICKETS_PERFUME.map((t) => (
                    <label key={t.valor} className={styles.ticketRadio}>
                      <input
                        type="radio"
                        name="ticketPromedio"
                        checked={ticketPromedio === t.valor}
                        onChange={() => setTicketPromedio(t.valor)}
                      />
                      <span>{formatLocal(convertCLP(t.valor))}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Mercado */}
            <div className={styles.bloqueMercado}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
              </div>
            </div>
          </div>
        )}

        {/* Market Share por Marca */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share por Marca</h4>
            <div className={styles.marketShareGrid}>
              {resultados.marketShareData.map((data) => (
                <div key={data.nombre} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{data.nombre}</div>
                  <div className={styles.cardShare}>
                    <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Clientes</span>
                      <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                    </div>
                    <div>
                      <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Tablas de desglose: Prioridades + Cantidad perfumes */}
        {resultados && (
          <div className={styles.tablasDesglose}>
            <div className={styles.desgloseContainer}>
              <h4>¿Qué priorizan al comprar?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Prioridad</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.prioridades).map(([prioridad, data]) => (
                    <tr key={prioridad}>
                      <td className="text-start">{prioridad}</td>
                      <td className="text-center">
                        <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.desgloseContainer}>
              <h4>¿Cuántos perfumes usa?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">Cantidad</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.cantidadPerfumes).map(([cantidad, data]) => (
                    <tr key={cantidad}>
                      <td className="text-center">{cantidad}</td>
                      <td className="text-center">
                        <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Ipsos 2025 (lámina 108, base 365 usuarios de perfumes / 400 total):</strong> 90,6% usa perfume;
            73,7% prioriza aroma, 13,1% duración; top marcas Rabanne (9,5%), Carolina Herrera (8,3%), Victoria Secret
            (7,3%).
          </p>
          <p>
            <strong>Supuestos comerciales:</strong> ticket promedio, compras por año por frecuencia y escenarios de
            captación.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Perfumes</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>Marca objetivo:</label>
                    <select
                      value={marcaSeleccionada}
                      onChange={(e) => setMarcaSeleccionada(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {Object.keys(IPSOS_PERFUMES.marcas).map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado (supuesto):</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION.map((p) => (
                        <option key={p.valor} value={p.valor}>
                          {p.valor}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad(marcaSeleccionada, porcentajeCaptacion);
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Inversión referencia (3 meses):</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(INVERSION_REFERENCIA_3M.valor))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Perfumes;
